/**
 * SurveyReport — Mobile-optimized draft report viewer/editor.
 * Route: /visits/:visitId/survey/report
 *
 * Displays generated survey draft reports with inline editing.
 * Fetches from and saves to Supabase Storage via the survey edge function.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Printer,
  AlertTriangle,
  Info,
  Grid3X3,
  Clock,
  Shield,
  BookOpen,
  Loader2,
  ShieldCheck,
  ClipboardList,
  Images,
  X,
} from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { invokeFunction } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { getVisitDetails } from '../../lib/services/visits'
import { getSurvey, getFindings, getWalkthroughState, type SurveyFinding, type SurveySession, type WalkthroughState } from '../../lib/services/survey'
import { surveyReportBackLabel, surveyReportBackTarget } from './surveyReportNavigation'
import { generateAiDraftReport, getReport, releaseReport, submitSurveyDraftReport } from '../../lib/services/reports'
import { downloadEvidenceBlob } from '../../lib/services/evidence'
import { surveyStore } from '../../lib/offline/surveyStore'
import {
  computeWalkthroughAssessmentCoverage,
  type AssessmentCoverage,
} from '../../lib/walkthroughAssessmentCoverage'

// ── Types ────────────────────────────────────────────────────────────────────

interface Finding {
  finding_id: string
  domain: string
  standard_ref: string
  severity: number
  risk: string
  scope: string
  area: string
  observation: string
  detailed_analysis: string
  image?: string
  image_file?: string
  evidence_url?: string
  evidence_refs?: string[]
  photo_pins?: EvidencePin[]
  checklist_item?: string
}

interface EvidencePin {
  photo_ref?: string
  pin_ref?: string
  x: number | null
  y: number | null
  severity?: number | null
  comment?: string
}

interface ImageAppendixItem {
  key: string
  label: string
  finding: Finding
  imageSrc?: string
  evidenceId?: string
  pin?: EvidencePin
}

const reportEvidenceImageCache = new Map<string, string>()
const IMAGE_APPENDIX_INITIAL_LOAD_COUNT = 8
const IMAGE_APPENDIX_LOAD_STEP = 8
const IMAGE_APPENDIX_LOAD_INTERVAL_MS = 350

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result)
      } else {
        reject(new Error('Evidence image could not be converted for display.'))
      }
    }
    reader.onerror = () => reject(reader.error || new Error('Evidence image could not be read.'))
    reader.readAsDataURL(blob)
  })
}

function evidencePreviewCacheKey(visitId: string, evidenceId: string): string {
  return `${visitId}:${evidenceId}`
}

function isDisplayableImageBlob(blob: Blob | null | undefined): blob is Blob {
  if (!(blob instanceof Blob)) return false
  if (blob.size <= 0) return false
  return !blob.type || blob.type.toLowerCase().startsWith('image/')
}

async function resolveReportEvidenceImage(visitId: string | undefined, imageSrc: string | undefined, evidenceId: string | undefined): Promise<string | null> {
  if (imageSrc) return imageSrc
  if (!visitId || !evidenceId) return null

  const cacheKey = evidencePreviewCacheKey(visitId, evidenceId)
  const memoryCached = reportEvidenceImageCache.get(cacheKey)
  if (memoryCached) return memoryCached

  const localPhoto = await surveyStore.getPhoto(evidenceId).catch(() => undefined)
  if (isDisplayableImageBlob(localPhoto?.blob)) {
    const dataUrl = await blobToDataUrl(localPhoto.blob)
    reportEvidenceImageCache.set(cacheKey, dataUrl)
    return dataUrl
  }

  const persisted = await surveyStore.getEvidencePreview(visitId, evidenceId).catch(() => undefined)
  if (persisted?.data_url) {
    reportEvidenceImageCache.set(cacheKey, persisted.data_url)
    return persisted.data_url
  }

  const blob = await downloadEvidenceBlob(visitId, evidenceId)
  if (!isDisplayableImageBlob(blob)) {
    throw new Error('Downloaded evidence did not contain an image payload.')
  }
  const dataUrl = await blobToDataUrl(blob)
  reportEvidenceImageCache.set(cacheKey, dataUrl)
  void surveyStore.saveEvidencePreview({
    visit_id: visitId,
    evidence_id: evidenceId,
    data_url: dataUrl,
  }).catch(() => {
    // Preview cache is a speed optimization only; backend evidence remains canonical.
  })
  return dataUrl
}

interface RFI {
  rfi_num: number
  standard: string
  ep: number
  description: string
  severity: string
  scope: string
}

interface AINarratives {
  executive_summary: string
  domain_analysis: string
  finding_narratives: string
  corrective_actions: string
  thirty_sixty_ninety: string
  action_items: string
}

interface ReportAssessmentCoverage {
  is_partial: boolean
  assessed_items: number
  not_assessed_items: number
  total_items: number
  scope_summary: string
  not_assessed_disclaimer: string
}

interface ReportData {
  generated_at: string
  facility: {
    name: string
    short_name?: string
    address?: string
    oracle_id?: string
    survey_dates?: string
    framework?: string
    survey_type?: string
  }
  summary: {
    total_findings: number
    severity_high: number
    severity_moderate: number
    severity_low: number
    total_rfi: number
  }
  findings: Finding[]
  tjc_rfis: RFI[]
  safer_matrix: Record<string, RFI[]>
  ai_narratives: AINarratives
  assessment_coverage?: ReportAssessmentCoverage
  draft_generation?: {
    status: 'generated' | 'edited' | 'non_ai_fallback' | 'failed'
    source: 'backend_ai' | 'field_edit' | 'local_fallback'
    generated_at?: string
    prompt_version?: string
  }
}

function toReportAssessmentCoverage(coverage: AssessmentCoverage): ReportAssessmentCoverage {
  return {
    is_partial: coverage.isPartial,
    assessed_items: coverage.assessedItems,
    not_assessed_items: coverage.notAssessedItems,
    total_items: coverage.totalItems,
    scope_summary: coverage.scopeSummary,
    not_assessed_disclaimer: coverage.notAssessedDisclaimer,
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const ACCREDITATION_FRAMEWORK_LABELS: Record<string, string> = {
  cms: 'CMS',
  aaahc: 'AAAHC',
  tjc: 'TJC',
  quad_a: 'QUAD A',
  achc: 'ACHC',
  state: 'State',
  other: 'Other',
}

function normalizeAccreditationFramework(value: unknown): string {
  if (typeof value !== 'string') return 'cms'
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_')
  if (normalized === 'cms' || normalized === 'aaahc' || normalized === 'tjc' || normalized === 'quad_a' || normalized === 'achc' || normalized === 'state' || normalized === 'other') {
    return normalized
  }
  if (normalized === 'quad' || normalized === 'quada') return 'quad_a'
  return 'cms'
}

function accreditationFrameworkLabel(framework: string): string {
  return ACCREDITATION_FRAMEWORK_LABELS[normalizeAccreditationFramework(framework)] ?? 'CMS'
}

function severityLabel(sev: number): string {
  if (sev >= 3) return 'High'
  if (sev === 2) return 'Moderate'
  return 'Low'
}

function severityColor(sev: number): string {
  if (sev >= 3) return 'bg-red-100 text-red-800 border-red-300'
  if (sev === 2) return 'bg-amber-100 text-amber-800 border-amber-300'
  return 'bg-emerald-100 text-emerald-800 border-emerald-300'
}

function severityBorder(sev: number): string {
  if (sev >= 3) return 'border-l-red-500'
  if (sev === 2) return 'border-l-amber-500'
  return 'border-l-emerald-500'
}

function severitySortValue(sev: number): number {
  if (sev >= 3) return 0
  if (sev === 2) return 1
  return 2
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : null
}

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function asNumber(value: unknown, fallback = 0): number {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : fallback
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map(asString).filter(Boolean)
}

function asEvidencePins(value: unknown): EvidencePin[] {
  if (!Array.isArray(value)) return []
  return value
    .map((entry): EvidencePin | null => {
      const pin = asRecord(entry)
      if (!pin) return null
      const x = Number(pin.x)
      const y = Number(pin.y)
      return {
        photo_ref: asString(pin.photo_ref) || undefined,
        pin_ref: asString(pin.pin_ref) || undefined,
        x: Number.isFinite(x) ? Math.max(0, Math.min(1, x)) : null,
        y: Number.isFinite(y) ? Math.max(0, Math.min(1, y)) : null,
        severity: Number.isFinite(Number(pin.severity)) ? Number(pin.severity) : null,
        comment: asString(pin.comment) || undefined,
      }
    })
    .filter((pin): pin is EvidencePin => Boolean(pin))
}

function isDisplayableImageSrc(value: string | undefined): value is string {
  return Boolean(value && (/^(https?:|blob:)/i.test(value) || /^data:image\//i.test(value)))
}

function imageSourceFromFinding(finding: Finding): string | undefined {
  const candidate = finding.image || finding.evidence_url
  return isDisplayableImageSrc(candidate) ? candidate : undefined
}

function isPlaceholderEvidenceRef(ref: string): boolean {
  return /^photo-ref-\d+$/i.test(ref)
}

function clampPinCoordinate(value: number | null | undefined): number {
  if (!Number.isFinite(Number(value))) return 0
  return Math.min(1, Math.max(0, Number(value)))
}

function containedImageFrameStyle(
  container: { width: number; height: number },
  natural: { width: number; height: number },
): { left: string; top: string; width: string; height: string } {
  if (container.width <= 0 || container.height <= 0 || natural.width <= 0 || natural.height <= 0) {
    return { left: '0%', top: '0%', width: '100%', height: '100%' }
  }

  const containerAspect = container.width / container.height
  const imageAspect = natural.width / natural.height
  if (imageAspect > containerAspect) {
    const height = (containerAspect / imageAspect) * 100
    return {
      left: '0%',
      top: `${(100 - height) / 2}%`,
      width: '100%',
      height: `${height}%`,
    }
  }

  const width = (imageAspect / containerAspect) * 100
  return {
    left: `${(100 - width) / 2}%`,
    top: '0%',
    width: `${width}%`,
    height: '100%',
  }
}

function findingMatchScore(reportFinding: Finding, surveyFinding: SurveyFinding): number {
  let score = 0
  if (reportFinding.finding_id && reportFinding.finding_id === surveyFinding.finding_id) score += 8
  if (reportFinding.standard_ref && reportFinding.standard_ref === surveyFinding.standard_ref) score += 4
  if (reportFinding.area && reportFinding.area === String(surveyFinding.area ?? '')) score += 2
  if (reportFinding.domain && reportFinding.domain === String(surveyFinding.domain ?? '')) score += 1
  if (reportFinding.observation && reportFinding.observation === surveyFinding.observation) score += 2
  return score
}

function walkthroughPinsForPhoto(walkthroughState: WalkthroughState | null, photoId: string): EvidencePin[] {
  if (!walkthroughState) return []
  return Object.values(walkthroughState.stepStates).flatMap((step) => {
    return Object.values(step.promptEvidence ?? {}).flatMap((evidence) => {
      const annotations = evidence.photoAnnotations?.[photoId] ?? []
      return annotations.map((annotation): EvidencePin => ({
        photo_ref: photoId,
        pin_ref: annotation.id,
        x: annotation.x,
        y: annotation.y,
        severity: annotation.severity,
        comment: annotation.comment,
      }))
    })
  })
}

function walkthroughNoteForPhoto(walkthroughState: WalkthroughState | null, photoId: string): string {
  if (!walkthroughState) return ''
  for (const step of Object.values(walkthroughState.stepStates)) {
    for (const evidence of Object.values(step.promptEvidence ?? {})) {
      if (evidence.photoIds.includes(photoId)) {
        return evidence.note || step.notes || ''
      }
    }
    if (step.photoIds.includes(photoId)) return step.notes || ''
  }
  return ''
}

function uniquePhotoIds(photoIds: string[]): string[] {
  const seen = new Set<string>()
  const resolved: string[] = []
  for (const photoId of photoIds.map(String).filter(Boolean)) {
    if (seen.has(photoId)) continue
    seen.add(photoId)
    resolved.push(photoId)
  }
  return resolved
}

function walkthroughPhotoIdsForFinding(walkthroughState: WalkthroughState | null, findingId: string): string[] {
  if (!walkthroughState || !findingId) return []
  const photoIds = Object.values(walkthroughState.stepStates).flatMap((step) => {
    if (!step.findingIds.includes(findingId)) return []
    const promptPhotoIds = Object.values(step.promptEvidence ?? {}).flatMap((evidence) => {
      const annotationPhotoIds = Object.keys(evidence.photoAnnotations ?? {})
      return [...evidence.photoIds, ...annotationPhotoIds]
    })
    return [...step.photoIds, ...promptPhotoIds]
  })
  return uniquePhotoIds(photoIds)
}

function resolveEvidenceRefs(
  reportFinding: Finding | null,
  surveyFinding: SurveyFinding,
  walkthroughState: WalkthroughState | null,
): string[] {
  const reportRefs = reportFinding?.evidence_refs ?? []
  const surveyRefs = Array.isArray(surveyFinding.photo_ids) ? surveyFinding.photo_ids.map(String).filter(Boolean) : []
  const reportUsableRefs = reportRefs.filter((ref) => !isPlaceholderEvidenceRef(ref))
  if (reportUsableRefs.length > 0) return uniquePhotoIds(reportUsableRefs)
  const surveyUsableRefs = surveyRefs.filter((ref) => !isPlaceholderEvidenceRef(ref))
  if (surveyUsableRefs.length > 0) return uniquePhotoIds(surveyUsableRefs)
  const walkthroughRefs = walkthroughPhotoIdsForFinding(walkthroughState, surveyFinding.finding_id)
  if (walkthroughRefs.length > 0) return walkthroughRefs
  return uniquePhotoIds(reportRefs.length > 0 ? reportRefs : surveyRefs)
}

function appendixItemPriority(item: ImageAppendixItem): number {
  if (isDisplayableImageSrc(item.imageSrc)) return item.pin ? 7 : 6
  if (item.key.startsWith('source-')) return item.pin ? 5 : 4
  if (item.evidenceId && !isPlaceholderEvidenceRef(item.evidenceId)) return item.pin ? 3 : 2
  if (item.pin?.comment) return 2
  if (item.evidenceId && isPlaceholderEvidenceRef(item.evidenceId)) return 0
  return 1
}

function mergeAppendixItems(primary: ImageAppendixItem[], fallback: ImageAppendixItem[]): ImageAppendixItem[] {
  const seen = new Set(primary.map((item) => item.evidenceId || item.imageSrc || item.key))
  const merged = [...primary]
  for (const item of fallback) {
    const key = item.evidenceId || item.imageSrc || item.key
    if (seen.has(key)) continue
    seen.add(key)
    merged.push(item)
  }
  return merged
    .map((item, index) => ({ item, index, priority: appendixItemPriority(item) }))
    .sort((a, b) => {
      if (b.priority !== a.priority) return b.priority - a.priority
      return a.index - b.index
    })
    .map(({ item }) => item)
}

function remapPinsToEvidenceRefs(pins: EvidencePin[], evidenceRefs: string[]): EvidencePin[] {
  if (pins.length === 0 || evidenceRefs.length === 0) return pins
  return pins.map((pin, index) => {
    if (pin.photo_ref && evidenceRefs.includes(pin.photo_ref)) return pin
    return {
      ...pin,
      photo_ref: evidenceRefs[index] || evidenceRefs[0],
    }
  })
}

function mergeSurveyEvidenceIntoReport(report: ReportData, surveyFindings: SurveyFinding[], walkthroughState: WalkthroughState | null = null): ReportData {
  if (surveyFindings.length === 0) return report
  const used = new Set<string>()
  const findings = report.findings.map((finding) => {
    const existingRefs = finding.evidence_refs || []
    const hasUsableRefs = existingRefs.some((ref) => !isPlaceholderEvidenceRef(ref))
    if (hasUsableRefs) {
      const existingPins = finding.photo_pins || []
      if (existingPins.length > 0) return finding
      return {
        ...finding,
        photo_pins: existingRefs.flatMap((ref) => walkthroughPinsForPhoto(walkthroughState, ref)),
      }
    }

    const best = surveyFindings
      .filter((candidate) => !used.has(candidate.finding_id) && resolveEvidenceRefs(finding, candidate, walkthroughState).length > 0)
      .map((candidate) => ({ candidate, score: findingMatchScore(finding, candidate) }))
      .sort((a, b) => b.score - a.score)[0]

    if (!best || best.score < 4) return finding
    used.add(best.candidate.finding_id)
    const resolvedRefs = resolveEvidenceRefs(finding, best.candidate, walkthroughState)
    const existingPins = finding.photo_pins || []
    const remappedPins = existingPins.length > 0
      ? remapPinsToEvidenceRefs(existingPins, resolvedRefs)
      : resolvedRefs.flatMap((photoId) => walkthroughPinsForPhoto(walkthroughState, String(photoId)))
    return {
      ...finding,
      evidence_refs: resolvedRefs,
      photo_pins: remappedPins,
    }
  })

  return { ...report, findings }
}

function buildSourceEvidenceAppendixItems(surveyFindings: SurveyFinding[], walkthroughState: WalkthroughState | null): ImageAppendixItem[] {
  return surveyFindings.flatMap((finding, findingIndex) => {
    const resolvedRefs = resolveEvidenceRefs(null, finding, walkthroughState)
    return resolvedRefs.map((photoId, photoIndex) => {
      const normalizedPhotoId = String(photoId)
      const note = walkthroughNoteForPhoto(walkthroughState, normalizedPhotoId)
      const pins = walkthroughPinsForPhoto(walkthroughState, normalizedPhotoId)
      const reportFinding: Finding = {
        finding_id: finding.finding_id,
        domain: String(finding.domain ?? ''),
        standard_ref: finding.standard_ref ?? '',
        severity: Number(finding.severity ?? 1),
        risk: severityLabel(Number(finding.severity ?? 1)),
        scope: 'Survey evidence',
        area: String(finding.area ?? ''),
        observation: finding.observation ?? note,
        detailed_analysis: note,
        evidence_refs: [normalizedPhotoId],
        photo_pins: pins,
      }
      return {
        key: `source-${finding.finding_id}-${normalizedPhotoId}-${photoIndex}`,
        label: `Image ${findingIndex + 1}.${photoIndex + 1}`,
        finding: reportFinding,
        evidenceId: normalizedPhotoId,
        pin: pins[0],
      }
    })
  })
}

function buildImageAppendixItems(findings: Finding[]): ImageAppendixItem[] {
  return findings.flatMap((finding, findingIndex) => {
    const refs = finding.evidence_refs || []
    const src = imageSourceFromFinding(finding)
    const pins = finding.photo_pins || []
    const refItems = refs.length > 0
      ? refs.flatMap((ref, refIndex): ImageAppendixItem[] => {
        const pin = pins.find((candidate) => candidate.photo_ref === ref) || pins[refIndex] || pins[0]
        const displayableRef = isDisplayableImageSrc(ref)
        if (!displayableRef && isPlaceholderEvidenceRef(ref) && !src) return []
        return [{
          key: `${finding.finding_id}-${ref}-${refIndex}`,
          label: `Image ${findingIndex + 1}.${refIndex + 1}`,
          finding,
          imageSrc: displayableRef ? ref : src,
          evidenceId: displayableRef ? undefined : ref,
          pin,
        }]
      })
      : []

    if (refItems.length > 0) return refItems
    if (!src && pins.length === 0) return []
    return [{
      key: `${finding.finding_id}-image`,
      label: `Image ${findingIndex + 1}`,
      finding,
      imageSrc: src,
      pin: pins[0],
    }]
  })
}

function emptyNarratives(): AINarratives {
  return {
    executive_summary: '',
    domain_analysis: '',
    finding_narratives: '',
    corrective_actions: '',
    thirty_sixty_ninety: '',
    action_items: '',
  }
}

function splitNumberedStatements(text: string): string {
  return text
    .replace(/\s+(?=\d+[.)]\s+)/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function splitNarrativeBlocks(text: string): string[] {
  const normalized = splitNumberedStatements(text)
  if (!normalized) return []
  return normalized
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
}

function splitSentences(text: string): string[] {
  return splitNumberedStatements(text)
    .split(/(?<=[.!?])\s+(?=(?:[A-Z]|\d+[.)]))/)
    .map((line) => line.trim())
    .filter(Boolean)
}

function cleanActionItem(text: string): string {
  return text
    .trim()
    .replace(/^[-*]\s+/, '')
    .replace(/^\d+[.)]\s+/, '')
    .replace(/^(Within|By)\s+(30|60|90)\s+days?,?\s*/i, '')
    .trim()
}

function extractChecklistItems(text: string): string[] {
  const lines = splitNumberedStatements(text)
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
  const explicitItems = lines
    .filter((line) => /^[-*]\s+/.test(line) || /^\d+[.)]\s+/.test(line))
    .map(cleanActionItem)
    .filter(Boolean)

  if (explicitItems.length > 0) return explicitItems
  return splitSentences(text).map(cleanActionItem).filter(Boolean)
}

function NarrativeDisplay({ text }: { text: string }) {
  const blocks = splitNarrativeBlocks(text)
  if (blocks.length === 0) return null
  return (
    <div className="space-y-3">
      {blocks.map((block, index) => (
        <p key={`${block.slice(0, 24)}-${index}`} className="text-sm leading-relaxed text-slate-700">
          {block}
        </p>
      ))}
    </div>
  )
}

const DOMAIN_PATTERNS: Array<{ pattern: RegExp; label: string }> = [
  { pattern: /\bInfection Control\s*\(IC\)|\bIC\b/i, label: 'Infection Control (IC)' },
  { pattern: /\bMedication\s*\(MED\)|\bMED\b/i, label: 'Medication Management (MED)' },
  { pattern: /\bLife Safety Code\s*\(LSC\)|\bLSC\b/i, label: 'Life Safety Code (LSC)' },
  { pattern: /\bEnvironmental\s*\(ENV\)|\bENV\b/i, label: 'Environment of Care (ENV)' },
  { pattern: /\bWaste Management\s*\(WASTE\)|\bWASTE\b/i, label: 'Waste Management (WASTE)' },
]

function detectDomainLabel(text: string): string {
  return DOMAIN_PATTERNS.find((entry) => entry.pattern.test(text))?.label || 'Cross-domain'
}

function DomainAnalysisDisplay({ text }: { text: string }) {
  const sentences = splitSentences(text)
  if (sentences.length === 0) return null

  const sections: Array<{ domain: string; narrative: string[] }> = []
  for (const sentence of sentences) {
    const domain = detectDomainLabel(sentence)
    const previous = sections[sections.length - 1]
    if (!previous || (domain !== 'Cross-domain' && domain !== previous.domain) || previous.domain === 'Cross-domain') {
      sections.push({ domain, narrative: [sentence] })
    } else {
      previous.narrative.push(sentence)
    }
  }

  return (
    <div className="space-y-4">
      {sections.map((section, index) => (
        <div key={`${section.domain}-${index}`} className="space-y-1.5">
          <p className="text-sm text-slate-900">
            <span className="font-bold">Domain:</span> {section.domain}
          </p>
          <p className="text-sm leading-relaxed text-slate-700">
            {section.narrative.join(' ')}
          </p>
        </div>
      ))}
    </div>
  )
}

function CorrectiveActionsDisplay({ text, findings }: { text: string; findings: Finding[] }) {
  const sorted = [...findings].sort((a, b) => severitySortValue(a.severity) - severitySortValue(b.severity))
  const groups = [
    { label: 'High', findings: sorted.filter((finding) => finding.severity >= 3) },
    { label: 'Moderate', findings: sorted.filter((finding) => finding.severity === 2) },
    { label: 'Low', findings: sorted.filter((finding) => finding.severity <= 1) },
  ].filter((group) => group.findings.length > 0)
  const summaryItems = extractChecklistItems(text)

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.label} className="space-y-2">
          <p className="text-sm font-bold text-slate-900">{group.label} Priority</p>
          <ul className="space-y-2">
            {group.findings.map((finding) => (
              <li key={finding.finding_id} className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm leading-relaxed text-slate-700">
                <span className="font-semibold text-slate-900">{finding.area || finding.domain}</span>
                {finding.standard_ref ? <span className="text-slate-500"> ({finding.standard_ref})</span> : null}
                {': Correct and verify sustained compliance for '}
                {finding.observation}
              </li>
            ))}
          </ul>
        </div>
      ))}
      {summaryItems.length > 0 && (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <p className="text-sm font-bold text-slate-900">Corrective Action Checklist</p>
          <ChecklistItems items={summaryItems} />
        </div>
      )}
      {sorted.length > 0 && (
        <div className="space-y-3 border-t border-slate-100 pt-3">
          <p className="text-sm font-bold text-slate-900">CMS-2567 Plan of Action</p>
          <div className="space-y-3">
            {sorted.map((finding) => (
              <div key={`poc-${finding.finding_id}`} className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
                <p className="font-semibold text-slate-900">
                  {severityLabel(finding.severity)}: {finding.standard_ref || finding.domain}
                </p>
                <dl className="mt-2 space-y-1 text-xs leading-relaxed text-slate-700">
                  <div>
                    <dt className="inline font-bold text-slate-800">Deficiency: </dt>
                    <dd className="inline">{finding.observation}</dd>
                  </div>
                  <div>
                    <dt className="inline font-bold text-slate-800">Plan of correction: </dt>
                    <dd className="inline">
                      Correct the cited condition in {finding.area || 'the affected area'}, remove or mitigate the immediate risk, and document staff notification, physical correction, and leadership verification.
                    </dd>
                  </div>
                  <div>
                    <dt className="inline font-bold text-slate-800">Responsible party: </dt>
                    <dd className="inline">Administrator or assigned department leader.</dd>
                  </div>
                  <div>
                    <dt className="inline font-bold text-slate-800">Completion timeframe: </dt>
                    <dd className="inline">{finding.severity >= 3 ? 'Immediate mitigation with completion target in the First 30 Days.' : finding.severity === 2 ? 'Completion target Within 60 Days.' : 'Completion target Within 90 Days.'}</dd>
                  </div>
                  <div>
                    <dt className="inline font-bold text-slate-800">Monitoring for sustained compliance: </dt>
                    <dd className="inline">Re-audit the area, retain evidence of correction, and report compliance status through QAPI until sustained compliance is verified.</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function HighSeverityExecutiveCallout({ findings }: { findings: Finding[] }) {
  const highFindings = findings.filter((finding) => finding.severity >= 3)
  if (highFindings.length === 0) return null
  return (
    <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
      <p className="text-sm font-bold text-red-900">High Severity Findings Requiring Immediate Attention</p>
      <ul className="mt-2 space-y-2">
        {highFindings.map((finding) => (
          <li key={`high-${finding.finding_id}`} className="text-xs leading-relaxed text-red-900">
            <span className="font-semibold">{finding.standard_ref || finding.domain}</span>
            {finding.area ? ` (${finding.area})` : ''}: {finding.observation}
          </li>
        ))}
      </ul>
    </div>
  )
}

function timelineBuckets(text: string): Array<{ label: string; items: string[] }> {
  const labels = ['First 30 Days', 'Within 60 Days', 'Within 90 Days']
  const buckets = labels.map((label) => ({ label, items: [] as string[] }))
  for (const sentence of splitSentences(text)) {
    const cleaned = cleanActionItem(sentence)
    if (!cleaned) continue
    if (/\b(60|sixty)\b/i.test(sentence)) buckets[1].items.push(cleaned)
    else if (/\b(90|ninety)\b/i.test(sentence)) buckets[2].items.push(cleaned)
    else buckets[0].items.push(cleaned)
  }
  return buckets.filter((bucket) => bucket.items.length > 0)
}

function TimelineDisplay({ text }: { text: string }) {
  const buckets = timelineBuckets(text)
  if (buckets.length === 0) return null
  return (
    <div className="space-y-4">
      {buckets.map((bucket) => (
        <div key={bucket.label} className="space-y-2">
          <p className="text-sm font-bold text-slate-900">{bucket.label}</p>
          <ChecklistItems items={bucket.items} />
        </div>
      ))}
    </div>
  )
}

function ChecklistItems({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={`${item.slice(0, 24)}-${index}`} className="flex items-start gap-2 text-sm leading-relaxed text-slate-700">
          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-sm border border-slate-400 bg-white" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  )
}

function normalizeSummary(value: unknown, findings: Finding[]): ReportData['summary'] {
  const summary = asRecord(value)
  const high = findings.filter((finding) => finding.severity >= 3).length
  const moderate = findings.filter((finding) => finding.severity === 2).length
  const low = findings.filter((finding) => finding.severity <= 1).length
  return {
    total_findings: asNumber(summary?.total_findings, findings.length),
    severity_high: asNumber(summary?.severity_high, high),
    severity_moderate: asNumber(summary?.severity_moderate, moderate),
    severity_low: asNumber(summary?.severity_low, low),
    total_rfi: asNumber(summary?.total_rfi, 0),
  }
}

export function mapFormalReportPayloadToSurveyDraft(reportPayload: Record<string, unknown>): ReportData {
  const facilityBranding = asRecord(reportPayload.facility_branding) || {}
  const reportMeta = asRecord(reportPayload.report_meta) || {}
  const visit = asRecord(reportPayload.visit) || {}
  const aiGeneration = asRecord(reportPayload.ai_generation) || asRecord(reportMeta.ai_generation) || {}
  const aiNarratives = {
    ...emptyNarratives(),
    ...(asRecord(reportPayload.ai_narratives) || {}),
  }
  const guidedInput = asRecord(reportPayload.guided_survey_report_input)
  const pinGroups = Array.isArray(guidedInput?.no_response_evidence)
    ? guidedInput.no_response_evidence.flatMap((entry) => asEvidencePins(asRecord(entry)?.photo_pins))
    : []
  const findings = Array.isArray(reportPayload.findings)
    ? reportPayload.findings.map((entry, index) => {
      const finding = asRecord(entry) || {}
      const evidenceRefs = [
        ...asStringArray(finding.evidence_refs),
        ...asStringArray(finding.photo_refs),
      ].filter(Boolean)
      const pins = [
        ...asEvidencePins(finding.photo_pins),
        ...asEvidencePins(finding.annotations),
        ...pinGroups.filter((pin) => pin.photo_ref && evidenceRefs.includes(pin.photo_ref)),
      ]
      return {
        finding_id: asString(finding.finding_id) || `finding-${index + 1}`,
        domain: asString(finding.domain) || 'OTHER',
        standard_ref: asString(finding.standard_ref) || asString(finding.standard_id),
        severity: asNumber(finding.severity, 1),
        risk: asString(finding.risk) || severityLabel(asNumber(finding.severity, 1)),
        scope: asString(finding.scope) || 'Limited',
        area: asString(finding.area),
        observation: asString(finding.observation) || asString(finding.summary),
        detailed_analysis: asString(finding.detailed_analysis),
        image: asString(finding.image_url) || asString(finding.image) || asString(finding.evidence_url) || undefined,
        image_file: asString(finding.image_file) || undefined,
        evidence_url: asString(finding.evidence_url) || undefined,
        evidence_refs: evidenceRefs,
        photo_pins: pins,
        checklist_item: asString(finding.checklist_item) || undefined,
      }
    })
    : []

  const framework = normalizeAccreditationFramework(
    facilityBranding.accreditation_framework || reportMeta.accreditation_framework || visit.accreditation_framework_snapshot,
  )

  return {
    generated_at: asString(aiGeneration.generated_at) || asString(reportMeta.generated_at) || new Date().toISOString(),
    facility: {
      name: asString(facilityBranding.facility_name) || 'Unknown Facility',
      short_name: asString(facilityBranding.org_display_name) || undefined,
      address: [facilityBranding.address, facilityBranding.city, facilityBranding.state, facilityBranding.zip_code].map(asString).filter(Boolean).join(', ') || undefined,
      oracle_id: asString(facilityBranding.license_number) || undefined,
      survey_dates: asString(visit.survey_date) || undefined,
      framework: accreditationFrameworkLabel(framework),
      survey_type: asString(visit.visit_type) || undefined,
    },
    summary: normalizeSummary(reportPayload.summary, findings),
    findings,
    tjc_rfis: Array.isArray(reportPayload.tjc_rfis) ? reportPayload.tjc_rfis as RFI[] : [],
    safer_matrix: asRecord(reportPayload.safer_matrix) as Record<string, RFI[]> || {},
    ai_narratives: {
      executive_summary: asString(aiNarratives.executive_summary),
      domain_analysis: asString(aiNarratives.domain_analysis),
      finding_narratives: asString(aiNarratives.finding_narratives),
      corrective_actions: asString(aiNarratives.corrective_actions),
      thirty_sixty_ninety: asString(aiNarratives.thirty_sixty_ninety),
      action_items: asString(aiNarratives.action_items),
    },
    draft_generation: {
      status: 'generated',
      source: 'backend_ai',
      generated_at: asString(aiGeneration.generated_at) || undefined,
      prompt_version: asString(aiGeneration.prompt_version) || undefined,
    },
  }
}

export function mapSurveyDraftToFormalReportPayload(report: ReportData, visitId: string): Record<string, unknown> {
  const generatedAt = new Date().toISOString()
  const accreditationFramework = normalizeAccreditationFramework(report.facility.framework)
  const frameworkLabel = accreditationFrameworkLabel(accreditationFramework)
  return {
    governance_context: {
      accreditation_framework: accreditationFramework,
      accreditation_framework_label: frameworkLabel,
      framework_source: 'field_survey_draft',
    },
    report_meta: {
      generated_at: generatedAt,
      schema_version: '2026.1',
      source: 'field_survey_draft',
      visit_id: visitId,
      accreditation_framework: accreditationFramework,
      accreditation_framework_label: frameworkLabel,
    },
    visit: {
      visit_id: visitId,
      visit_type: report.facility.survey_type ?? null,
      survey_date: report.facility.survey_dates ?? null,
      accreditation_framework_snapshot: accreditationFramework,
      accreditation_framework_label: frameworkLabel,
    },
    score: {
      overall_score: null,
      grade: null,
      cms_outcome: null,
      cms_outcome_label: null,
    },
    executive_summary: report.ai_narratives.executive_summary,
    domain_analysis: report.ai_narratives.domain_analysis,
    corrective_actions_summary: report.ai_narratives.corrective_actions,
    action_items_summary: report.ai_narratives.action_items,
    summary: report.summary,
    findings: report.findings.map((finding) => ({
      finding_id: finding.finding_id,
      domain: finding.domain,
      standard_id: finding.standard_ref,
      standard_ref: finding.standard_ref,
      severity: finding.severity,
      condition_level: finding.severity >= 3,
      summary: finding.observation,
      observation: finding.observation,
      detailed_analysis: finding.detailed_analysis,
      area: finding.area,
      risk: finding.risk,
      scope: finding.scope,
      image_url: finding.image ?? null,
      image_file: finding.image_file ?? null,
      evidence_url: finding.evidence_url ?? null,
      evidence_refs: finding.evidence_refs ?? [],
      photo_pins: finding.photo_pins ?? [],
      checklist_item: finding.checklist_item ?? null,
      status: 'draft',
    })),
    tjc_rfis: report.tjc_rfis,
    safer_matrix: report.safer_matrix,
    ai_narratives: report.ai_narratives,
    ai_generation: report.draft_generation ? {
      generation_status: report.draft_generation.status,
      source: report.draft_generation.source,
      generated_at: report.draft_generation.generated_at ?? null,
      prompt_version: report.draft_generation.prompt_version ?? null,
      draft_only: true,
    } : undefined,
    facility_branding: {
      facility_name: report.facility.name,
      org_display_name: report.facility.short_name ?? null,
      address: report.facility.address ?? null,
      license_number: report.facility.oracle_id ?? null,
      logo_url: null,
      primary_color: null,
      accreditation_framework: accreditationFramework,
      accreditation_framework_label: frameworkLabel,
    },
  }
}

const SAFER_CELL_COLOR: Record<string, string> = {
  'High/Pattern': 'bg-red-600 text-white',
  'High/Widespread': 'bg-red-700 text-white',
  'High/Limited': 'bg-red-400 text-white',
  'Moderate/Pattern': 'bg-amber-500 text-white',
  'Moderate/Widespread': 'bg-amber-600 text-white',
  'Moderate/Limited': 'bg-amber-300 text-amber-900',
  'Low/Pattern': 'bg-emerald-400 text-white',
  'Low/Widespread': 'bg-emerald-500 text-white',
  'Low/Limited': 'bg-emerald-200 text-emerald-900',
}

// ── Collapsible Section ──────────────────────────────────────────────────────

function Section({
  title,
  icon: Icon,
  defaultOpen = false,
  children,
}: {
  title: string
  icon: React.ElementType
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="app-surface overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
      >
        <Icon size={18} className="text-[var(--brand-primary)] flex-shrink-0" />
        <span className="flex-1 text-sm font-bold text-slate-800">{title}</span>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  )
}

// ── Editable Text Block ──────────────────────────────────────────────────────

function EditableText({
  value,
  onChange,
  className = '',
  renderDisplay,
}: {
  value: string
  onChange: (v: string) => void
  className?: string
  renderDisplay?: (value: string) => React.ReactNode
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    setDraft(value)
  }, [value])

  useEffect(() => {
    if (editing && ref.current) {
      ref.current.focus()
      ref.current.style.height = 'auto'
      ref.current.style.height = ref.current.scrollHeight + 'px'
    }
  }, [editing])

  const handleBlur = () => {
    setEditing(false)
    if (draft !== value) {
      onChange(draft)
    }
  }

  if (editing) {
    return (
      <textarea
        ref={ref}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value)
          e.target.style.height = 'auto'
          e.target.style.height = e.target.scrollHeight + 'px'
        }}
        onBlur={handleBlur}
        className={`w-full border border-blue-300 rounded-lg p-3 text-sm text-slate-700 leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-blue-400 ${className}`}
      />
    )
  }

  return (
    <div
      onClick={() => setEditing(true)}
      className={`whitespace-pre-wrap text-sm text-slate-700 leading-relaxed cursor-text hover:bg-blue-50 rounded-lg p-2 -m-2 transition-colors ${className}`}
      title="Tap to edit"
    >
      {value
        ? renderDisplay ? renderDisplay(value) : <NarrativeDisplay text={value} />
        : <span className="italic text-slate-400">Tap to add content...</span>}
    </div>
  )
}

function ImageWithPin({
  visitId,
  item,
  shouldLoad,
  className = '',
}: {
  visitId?: string
  item: ImageAppendixItem
  shouldLoad?: boolean
  className?: string
}) {
  const [resolvedSrc, setResolvedSrc] = useState<string | null>(item.imageSrc || null)
  const [failed, setFailed] = useState(false)
  const [isVisible, setIsVisible] = useState(
    () => shouldLoad || Boolean(item.imageSrc) || typeof IntersectionObserver === 'undefined',
  )
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 })
  const containerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (shouldLoad || isVisible) return
    const node = containerRef.current
    if (!node) return

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setIsVisible(true)
        observer.disconnect()
      }
    }, { rootMargin: '300px' })
    observer.observe(node)
    return () => observer.disconnect()
  }, [isVisible, shouldLoad])

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const updateSize = () => {
      setContainerSize({
        width: node.clientWidth,
        height: node.clientHeight,
      })
    }
    updateSize()

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateSize)
      return () => window.removeEventListener('resize', updateSize)
    }

    const observer = new ResizeObserver(updateSize)
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!(shouldLoad || isVisible)) return

    let active = true
    resolveReportEvidenceImage(visitId, item.imageSrc, item.evidenceId)
      .then((dataUrl) => {
        if (!active) return
        if (dataUrl) setResolvedSrc(dataUrl)
      })
      .catch(() => {
        if (active) setFailed(true)
      })

    return () => {
      active = false
    }
  }, [isVisible, item.evidenceId, item.imageSrc, shouldLoad, visitId])

  const hasPin = item.pin && item.pin.x !== null && item.pin.y !== null
  const imageFrameStyle = containedImageFrameStyle(containerSize, naturalSize)
  return (
    <div ref={containerRef} className={`relative overflow-hidden rounded-lg border border-slate-200 bg-slate-100 ${className}`}>
      {resolvedSrc ? (
        <div
          data-testid="image-appendix-image-frame"
          className="absolute"
          style={imageFrameStyle}
        >
          <img
            src={resolvedSrc}
            alt={item.label}
            data-testid="image-appendix-thumbnail"
            className="h-full w-full object-contain"
            onError={() => {
              if (visitId && item.evidenceId) {
                reportEvidenceImageCache.delete(evidencePreviewCacheKey(visitId, item.evidenceId))
              }
              setResolvedSrc(null)
              setFailed(true)
            }}
            onLoad={(event) => {
              setNaturalSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }}
          />
          {hasPin && (
            <span
              className="absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-red-600 shadow-lg"
              style={{
                left: `${clampPinCoordinate(item.pin!.x) * 100}%`,
                top: `${clampPinCoordinate(item.pin!.y) * 100}%`,
              }}
              aria-label="Evidence pin"
              data-testid="image-appendix-pin"
              data-photo-ref={item.pin?.photo_ref || undefined}
              data-finding-id={item.finding.finding_id || undefined}
            >
              <span className="absolute left-1/2 top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
            </span>
          )}
        </div>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-slate-100 text-center text-[11px] font-semibold text-slate-500">
          {failed ? 'Image unavailable' : 'Evidence retained'}
        </div>
      )}
    </div>
  )
}

function ImagesAppendix({
  visitId,
  items,
}: {
  visitId?: string
  items: ImageAppendixItem[]
}) {
  const [selected, setSelected] = useState<ImageAppendixItem | null>(null)
  const [loadCount, setLoadCount] = useState(IMAGE_APPENDIX_INITIAL_LOAD_COUNT)

  useEffect(() => {
    if (loadCount >= items.length) return
    const timer = window.setTimeout(() => {
      setLoadCount((current) => Math.min(current + IMAGE_APPENDIX_LOAD_STEP, items.length))
    }, IMAGE_APPENDIX_LOAD_INTERVAL_MS)
    return () => window.clearTimeout(timer)
  }, [items.length, loadCount])

  return (
    <>
      <Section title={`Images Appendix (${items.length})`} icon={Images} defaultOpen>
        {items.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-600">
            No captured image evidence is available in this draft yet. If the survey included photos, regenerate the report or re-enter the completed walkthrough to confirm image evidence synced.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map((item, index) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setSelected(item)}
                data-testid="image-appendix-item"
                data-evidence-id={item.evidenceId || undefined}
                data-finding-id={item.finding.finding_id || undefined}
                className="overflow-hidden rounded-lg border border-slate-200 bg-white text-left shadow-sm transition-colors hover:border-[var(--brand-primary)]"
              >
                <ImageWithPin visitId={visitId} item={item} shouldLoad={index < loadCount} className="aspect-[4/3]" />
                <div className="space-y-1 p-2">
                  <p className="text-xs font-bold text-slate-900">{item.label}</p>
                  <p className="truncate text-[11px] text-slate-500">{item.finding.area || item.finding.domain}</p>
                  <p className="truncate font-mono text-[10px] text-slate-500">{item.finding.standard_ref || 'CMS tag pending'}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </Section>

      {selected && (
        <div className="fixed inset-0 z-[120] flex items-end bg-slate-950/70 p-3 sm:items-center sm:justify-center print:hidden">
          <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 p-3">
              <div>
                <p className="text-sm font-bold text-slate-900">Images Appendix</p>
                <p className="text-xs text-slate-500">{selected.finding.area || selected.finding.domain}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
                aria-label="Close image appendix"
              >
                <X size={18} />
              </button>
            </div>
            <div className="space-y-3 p-3">
              <ImageWithPin visitId={visitId} item={selected} shouldLoad className="aspect-[4/3]" />
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">CMS Tag</p>
                <p className="font-mono text-sm font-semibold text-slate-800">{selected.finding.standard_ref || 'CMS tag pending'}</p>
              </div>
              {selected.pin?.comment && (
                <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pin Note</p>
                  <p className="text-sm leading-relaxed text-slate-700">{selected.pin.comment}</p>
                </div>
              )}
              <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Updated Notes</p>
                <p className="text-sm leading-relaxed text-slate-700">
                  {selected.finding.detailed_analysis || selected.finding.observation || 'No updated notes recorded for this image.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Main Component ───────────────────────────────────────────────────────────

export function SurveyReport() {
  const { visitId } = useParams<{ visitId: string }>()
  const navigate = useNavigate()
  const { viewerContext } = useAuth()
  const backTo = surveyReportBackTarget(visitId, viewerContext?.role)
  const backLabel = surveyReportBackLabel(viewerContext?.role)
  const [report, setReport] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [sourceSurvey, setSourceSurvey] = useState<SurveySession | null>(null)
  const [sourceSurveyFindings, setSourceSurveyFindings] = useState<SurveyFinding[]>([])
  const [sourceWalkthroughState, setSourceWalkthroughState] = useState<WalkthroughState | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null)
  const [submittedReportId, setSubmittedReportId] = useState<string | null>(null)
  const [generatingAiDraft, setGeneratingAiDraft] = useState(false)
  const [generationError, setGenerationError] = useState<string | null>(null)
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Fetch report. If no draft exists yet, build a minimal placeholder from
  // the visit's findings + facility and persist it, so the SME sees their
  // walkthrough results instead of a 404. Full narratives are backfilled
  // later by the ASC-Evaluator pipeline when it runs.
  useEffect(() => {
    if (!visitId) return
    const load = async () => {
      setLoading(true)
      try {
        const fetched = await invokeFunction<ReportData>('survey', {
          method: 'GET',
          path: `/visits/${visitId}/report`,
        })
        const reportSurvey = await getSurvey(visitId, { preferCompleted: true }).catch(() => null)
          || await getSurvey(visitId).catch(() => null)
        setSourceSurvey(reportSurvey)
        const surveyFindings = reportSurvey ? await getFindings(visitId, reportSurvey.survey_id).catch(() => []) : []
        const walkthroughState = reportSurvey ? await getWalkthroughState(visitId, reportSurvey.survey_id).catch(() => null) : null
        setSourceSurveyFindings(surveyFindings)
        setSourceWalkthroughState(walkthroughState)
        if (fetched.data) {
          setReport(mergeSurveyEvidenceIntoReport(fetched.data, surveyFindings, walkthroughState))
          return
        }
        const msg = (fetched.error?.message || '').toLowerCase()
        const notFound = msg.includes('not found') || msg.includes('no draft report') || msg.includes('404')
        if (!notFound) {
          setError(fetched.error?.message ?? 'Failed to load draft report')
          return
        }
        // Auto-generate a minimal draft from survey findings + facility info
        const visit = await getVisitDetails(visitId)
        const findings = surveyFindings
        const mappedFindings: Finding[] = findings.map((f) => ({
          finding_id: f.finding_id,
          domain: String(f.domain ?? ''),
          standard_ref: f.standard_ref ?? '',
          severity: Number(f.severity ?? 1),
          risk: 'Pending review',
          scope: 'Limited',
          area: String(f.area ?? ''),
          observation: f.observation ?? '',
          detailed_analysis: '',
          evidence_refs: Array.isArray(f.photo_ids) ? f.photo_ids.map(String).filter(Boolean) : [],
        }))
        const summary = {
          total_findings: mappedFindings.length,
          severity_high: mappedFindings.filter((f) => f.severity >= 3).length,
          severity_moderate: mappedFindings.filter((f) => f.severity === 2).length,
          severity_low: mappedFindings.filter((f) => f.severity <= 1).length,
          total_rfi: 0,
        }
        const coverage = computeWalkthroughAssessmentCoverage(walkthroughState?.stepStates ?? null)
        const assessmentCoverage = toReportAssessmentCoverage(coverage)
        const partialScopeNote = coverage.isPartial
          ? ` ${coverage.notAssessedDisclaimer}`
          : ''
        const draft: ReportData = {
          generated_at: new Date().toISOString(),
          facility: {
            name: visit.facility_name || 'Unknown Facility',
            address: [visit.address, visit.city, visit.state, visit.zip_code].filter(Boolean).join(', ') || undefined,
          },
          summary,
          findings: mappedFindings,
          tjc_rfis: [],
          safer_matrix: {},
          assessment_coverage: assessmentCoverage,
          ai_narratives: {
            executive_summary:
              `Draft generated from walkthrough findings (${coverage.assessedItems} of ${coverage.totalItems} items assessed).`
              + partialScopeNote
              + ' Narrative summary will be produced when the report pipeline runs against this visit.',
            domain_analysis: '',
            finding_narratives: '',
            corrective_actions: '',
            thirty_sixty_ninety: '',
            action_items: '',
          },
          draft_generation: {
            status: 'non_ai_fallback',
            source: 'local_fallback',
            generated_at: new Date().toISOString(),
          },
        }
        const recoveredDraft = mergeSurveyEvidenceIntoReport(draft, surveyFindings, walkthroughState)
        // Persist so future loads skip the auto-gen path
        await invokeFunction('survey', {
          method: 'PUT',
          path: `/visits/${visitId}/report`,
          body: recoveredDraft,
        })
        setReport(recoveredDraft)
      } catch (genErr) {
        setError(genErr instanceof Error ? genErr.message : 'Unable to load draft report')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [visitId])

  const persistSurveyReport = useCallback(async (updated: ReportData) => {
    if (!visitId) return
    await invokeFunction('survey', {
      method: 'PUT',
      path: `/visits/${visitId}/report`,
      body: updated,
    })
  }, [visitId])

  // Debounced auto-save
  const autoSave = useCallback(
    (updated: ReportData) => {
      if (!visitId) return
      if (saveTimeout.current) clearTimeout(saveTimeout.current)
      saveTimeout.current = setTimeout(async () => {
        setSaving(true)
        await persistSurveyReport(updated)
        setSaving(false)
      }, 1500)
    },
    [visitId, persistSurveyReport],
  )

  const updateNarrative = (key: keyof AINarratives, value: string) => {
    if (!report) return
    const updated = {
      ...report,
      ai_narratives: { ...report.ai_narratives, [key]: value },
      draft_generation: {
        ...(report.draft_generation || { source: 'field_edit' as const }),
        status: 'edited' as const,
        source: 'field_edit' as const,
      },
    }
    setReport(updated)
    autoSave(updated)
  }

  const updateFinding = (idx: number, field: keyof Finding, value: string) => {
    if (!report) return
    const findings = [...report.findings]
    findings[idx] = { ...findings[idx], [field]: value }
    const updated = {
      ...report,
      findings,
      draft_generation: {
        ...(report.draft_generation || { source: 'field_edit' as const }),
        status: 'edited' as const,
        source: 'field_edit' as const,
      },
    }
    setReport(updated)
    autoSave(updated)
  }

  const handleGenerateAiDraft = useCallback(async () => {
    if (!visitId || !report) return
    setGeneratingAiDraft(true)
    setGenerationError(null)
    setSubmitSuccess(null)
    try {
      if (saveTimeout.current) clearTimeout(saveTimeout.current)
      await persistSurveyReport(report)
      const currentPayload = mapSurveyDraftToFormalReportPayload(report, visitId)
      await submitSurveyDraftReport(visitId, currentPayload)
      const generated = await generateAiDraftReport(visitId)
      const detail = await getReport(generated.report_id)
      const generatedPayload = detail.report_payload
      if (!generatedPayload) throw new Error('Missing generated report payload')
      const reportSurvey = await getSurvey(visitId, { preferCompleted: true }).catch(() => null)
        || await getSurvey(visitId).catch(() => null)
      setSourceSurvey(reportSurvey)
      const surveyFindings = reportSurvey ? await getFindings(visitId, reportSurvey.survey_id).catch(() => []) : []
      const walkthroughState = reportSurvey ? await getWalkthroughState(visitId, reportSurvey.survey_id).catch(() => null) : null
      setSourceSurveyFindings(surveyFindings)
      setSourceWalkthroughState(walkthroughState)
      const generatedDraft = mergeSurveyEvidenceIntoReport(mapFormalReportPayloadToSurveyDraft(generatedPayload), surveyFindings, walkthroughState)
      await persistSurveyReport(generatedDraft)
      setReport(generatedDraft)
    } catch (_err) {
      setGenerationError('Draft generation is unavailable. Continue editing this draft or try again.')
      setReport((current) => current
        ? mergeSurveyEvidenceIntoReport({
          ...current,
          draft_generation: {
            ...(current.draft_generation || { source: 'local_fallback' as const }),
            status: 'failed',
          },
        }, sourceSurveyFindings, sourceWalkthroughState)
        : current)
    } finally {
      setGeneratingAiDraft(false)
    }
  }, [visitId, report, persistSurveyReport, sourceSurveyFindings, sourceWalkthroughState])

  const handleSubmitForApproval = useCallback(async () => {
    if (!visitId || !report) return
    setSubmitting(true)
    setSubmitError(null)
    setSubmitSuccess(null)
    try {
      if (saveTimeout.current) clearTimeout(saveTimeout.current)
      const payload = mapSurveyDraftToFormalReportPayload(report, visitId)
      const submitted = await submitSurveyDraftReport(visitId, payload)
      setSubmittedReportId(submitted.report_id)
      if (viewerContext?.role === 'FieldSurveyor') {
        try {
          await releaseReport(submitted.report_id)
          setSubmitSuccess('Report released. Final PDF was generated. No email was sent from this step; final PDF email delivery happens from report delivery.')
        } catch (_releaseErr) {
          setSubmitSuccess('Report submitted. Approval or release remains pending. No email was sent from this step; final PDF email delivery happens after release.')
        }
        return
      }
      navigate(`/reports/${submitted.report_id}`)
    } catch (_err) {
      setSubmitError('Unable to submit the draft report. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [visitId, report, navigate, viewerContext?.role])

  // Loading state
  if (loading) {
    return (
      <FieldAppShell eyebrow="Survey" title="Draft Report" backTo={backTo} backLabel={backLabel}>
        <div className="app-surface mx-4 mt-6 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]" />
          <p className="text-sm font-semibold text-slate-700">Loading report workspace...</p>
          <p className="text-xs text-slate-500">Your survey findings are being prepared for review.</p>
        </div>
      </FieldAppShell>
    )
  }

  if (error || !report) {
    return (
      <FieldAppShell eyebrow="Survey" title="Draft Report" backTo={backTo} backLabel={backLabel}>
        <div className="app-surface p-6 mx-4 mt-6 text-center space-y-3">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <p className="text-sm text-red-600">{error || 'Report not found'}</p>
        </div>
      </FieldAppShell>
    )
  }

  const { facility, summary, findings, ai_narratives, safer_matrix, tjc_rfis } = report
  const liveCoverage = sourceWalkthroughState
    ? toReportAssessmentCoverage(computeWalkthroughAssessmentCoverage(sourceWalkthroughState.stepStates))
    : null
  const assessmentCoverage = report.assessment_coverage ?? liveCoverage
  const isTjcAligned = normalizeAccreditationFramework(facility.framework) === 'tjc'
  const reportSubmitted = submittedReportId !== null
  const imageAppendixItems = mergeAppendixItems(
    buildImageAppendixItems(findings),
    buildSourceEvidenceAppendixItems(sourceSurveyFindings, sourceWalkthroughState),
  )
  const guidedSurveyResumeTarget = visitId ? `/visits/${visitId}/survey/walkthrough?resume=completed` : '../walkthrough'

  return (
    <FieldAppShell eyebrow="Survey" title="Draft Report" backTo={backTo} backLabel={backLabel}>
    <div className="space-y-3 px-4 py-6 max-w-[430px] mx-auto print:max-w-none print:px-8">
      {/* Header */}
      <div className="app-surface p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-slate-900 leading-tight">
              {facility.short_name || facility.name}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">{facility.name}</p>
          </div>
          <div className="flex items-center gap-1.5">
            {saving && (
              <span className="text-[10px] text-blue-500 font-medium animate-pulse">
                Saving...
              </span>
            )}
            <button
              type="button"
              onClick={() => window.print()}
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors print:hidden"
              title="Share as PDF"
            >
              <Printer size={18} className="text-slate-600" />
            </button>
          </div>
        </div>
        <div className="pt-2">
          <button
            type="button"
            onClick={handleSubmitForApproval}
            disabled={submitting || generatingAiDraft || reportSubmitted}
            className="btn-primary w-full py-2.5 text-xs disabled:opacity-60 print:hidden"
          >
            {submitting ? (
              <><Loader2 size={14} className="inline mr-1.5 animate-spin" /> Submitting draft...</>
            ) : reportSubmitted ? (
              <><ShieldCheck size={14} className="inline mr-1.5" /> Report Submitted</>
            ) : (
              <><ShieldCheck size={14} className="inline mr-1.5" /> Submit for Approval</>
            )}
          </button>
          {submitError && (
            <p className="mt-2 text-xs text-red-600" data-testid="survey-report-submit-error">{submitError}</p>
          )}
          {submitSuccess && (
            <p className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold leading-relaxed text-emerald-800" data-testid="survey-report-submit-success">
              {submitSuccess}
            </p>
          )}
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2 print:hidden">
          {!reportSubmitted && (
            <Link
              to={guidedSurveyResumeTarget}
              data-testid="survey-report-enter-guided-survey"
              data-survey-status={sourceSurvey?.status ?? 'unknown'}
              className="btn-secondary flex w-full items-center justify-center py-2.5 text-xs"
            >
              <ClipboardList size={14} className="mr-1.5" /> Enter Guided Survey
            </Link>
          )}
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-xs font-bold text-slate-800">Draft Status</p>
              <p className="text-[11px] text-slate-500" data-testid="survey-report-generation-status">
                {report.draft_generation?.status === 'generated'
                  ? 'Generated draft'
                  : report.draft_generation?.status === 'edited'
                    ? 'Edited draft'
                    : report.draft_generation?.status === 'failed'
                      ? 'Generation failed'
                      : 'Walkthrough fallback draft'}
              </p>
            </div>
            <button
              type="button"
              onClick={handleGenerateAiDraft}
              disabled={generatingAiDraft || submitting || reportSubmitted}
              className="btn-secondary px-3 py-2 text-[11px] disabled:opacity-60"
              data-testid="survey-report-generate-ai-draft"
            >
              {generatingAiDraft ? (
                <><Loader2 size={13} className="inline mr-1 animate-spin" /> Generating report...</>
              ) : report.draft_generation?.status === 'generated' ? (
                'Regenerate Report'
              ) : (
                'Generate Report'
              )}
            </button>
          </div>
          {generationError && (
            <p className="text-xs text-red-600" data-testid="survey-report-generation-error">{generationError}</p>
          )}
        </div>

        {facility.survey_dates && (
          <p className="text-xs text-slate-500">Survey: {facility.survey_dates}</p>
        )}
        {facility.framework && (
          <span className="brand-chip">{facility.framework}</span>
        )}

        {/* Summary chips */}
        <div className="flex flex-wrap gap-1.5 pt-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700">
            {summary.total_findings} findings
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-red-100 text-red-700">
            {summary.severity_high} high
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-700">
            {summary.severity_moderate} moderate
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-700">
            {summary.severity_low} low
          </span>
          {isTjcAligned && summary.total_rfi > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-700">
              {summary.total_rfi} RFIs
            </span>
          )}
          {assessmentCoverage && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                assessmentCoverage.is_partial ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
              }`}
              data-testid="report-coverage-chip"
            >
              {assessmentCoverage.assessed_items}/{assessmentCoverage.total_items} assessed
            </span>
          )}
        </div>
      </div>

      {assessmentCoverage?.is_partial && (
        <div
          className="app-surface border border-amber-200 bg-amber-50 p-3 space-y-1"
          data-testid="report-partial-assessment-banner"
          role="note"
        >
          <p className="text-xs font-bold text-amber-900">Partial assessment — scope limitation</p>
          <p className="text-[11px] text-amber-900 leading-relaxed">
            {assessmentCoverage.not_assessed_disclaimer}
          </p>
          <p className="text-[11px] text-slate-700">{assessmentCoverage.scope_summary}</p>
        </div>
      )}

      {/* Executive Summary */}
      <Section title="Executive Summary" icon={FileText} defaultOpen>
        <HighSeverityExecutiveCallout findings={findings} />
        <EditableText
          value={ai_narratives.executive_summary}
          onChange={(v) => updateNarrative('executive_summary', v)}
        />
      </Section>

      {/* TJC-only SAFER Matrix */}
      {isTjcAligned && (
        <Section title="SAFER Matrix" icon={Grid3X3}>
          <div className="overflow-x-auto -mx-1">
            <div className="space-y-2 min-w-[320px]">
              {Object.entries(safer_matrix).map(([cell, rfis]) => {
                const colorClass = SAFER_CELL_COLOR[cell] || 'bg-slate-200 text-slate-800'
                return (
                  <div key={cell} className="space-y-1">
                    <div className={`inline-block px-2.5 py-1 rounded-md text-xs font-bold ${colorClass}`}>
                      {cell} ({rfis.length})
                    </div>
                    <div className="space-y-0.5 pl-2">
                      {rfis.map((rfi) => (
                        <p key={rfi.rfi_num} className="text-xs text-slate-600">
                          <span className="font-mono font-bold text-slate-800">
                            {rfi.standard} EP{rfi.ep}
                          </span>{' '}
                          &mdash; {rfi.description}
                        </p>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </Section>
      )}

      {/* Findings */}
      <Section title={`Findings (${findings.length})`} icon={AlertTriangle}>
        <div className="space-y-3">
          {findings.map((f, idx) => (
            <div
              key={f.finding_id}
              className={`border-l-4 ${severityBorder(f.severity)} rounded-lg bg-white shadow-sm overflow-hidden`}
            >
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs font-bold text-slate-800">
                    {f.finding_id}
                  </span>
                  <span
                    className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border ${severityColor(f.severity)}`}
                  >
                    {f.risk || severityLabel(f.severity)}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {f.domain.split(',').map((d) => (
                    <span key={d.trim()} className="brand-chip text-[10px]">
                      {d.trim()}
                    </span>
                  ))}
                  <span className="text-[10px] text-slate-400 px-1">{f.area}</span>
                </div>
                <p className="text-[11px] text-slate-500 font-mono">{f.standard_ref}</p>
                <EditableText
                  value={f.observation}
                  onChange={(v) => updateFinding(idx, 'observation', v)}
                />
                {f.detailed_analysis && (
                  <div className="bg-slate-50 rounded-md p-2 mt-1">
                    <EditableText
                      value={f.detailed_analysis}
                      onChange={(v) => updateFinding(idx, 'detailed_analysis', v)}
                      className="text-xs"
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Domain Analysis */}
      <Section title="Domain Analysis" icon={BookOpen}>
        <EditableText
          value={ai_narratives.domain_analysis}
          onChange={(v) => updateNarrative('domain_analysis', v)}
          renderDisplay={(value) => <DomainAnalysisDisplay text={value} />}
        />
      </Section>

      {/* Corrective Actions */}
      <Section title="Corrective Actions" icon={Shield}>
        <EditableText
          value={ai_narratives.corrective_actions}
          onChange={(v) => updateNarrative('corrective_actions', v)}
          renderDisplay={(value) => <CorrectiveActionsDisplay text={value} findings={findings} />}
        />
      </Section>

      {/* 30/60/90 Timeline */}
      <Section title="30/60/90 Day Timeline" icon={Clock}>
        <EditableText
          value={ai_narratives.thirty_sixty_ninety}
          onChange={(v) => updateNarrative('thirty_sixty_ninety', v)}
          renderDisplay={(value) => <TimelineDisplay text={value} />}
        />
      </Section>

      <ImagesAppendix visitId={visitId} items={imageAppendixItems} />

      {/* TJC RFIs */}
      {isTjcAligned && tjc_rfis.length > 0 && (
        <Section title={`TJC RFIs (${tjc_rfis.length})`} icon={Info}>
          <div className="space-y-2">
            {tjc_rfis.map((rfi) => (
              <div key={rfi.rfi_num} className="flex items-start gap-2 py-1">
                <span className="text-xs font-mono font-bold text-slate-500 w-6 flex-shrink-0">
                  {rfi.rfi_num}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-700">
                    <span className="font-bold">{rfi.standard}</span> EP{rfi.ep} &mdash;{' '}
                    {rfi.description}
                  </p>
                  <div className="flex gap-1.5 mt-0.5">
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${
                        rfi.severity === 'High'
                          ? 'bg-red-100 text-red-700'
                          : rfi.severity === 'Moderate'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}
                    >
                      {rfi.severity}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                      {rfi.scope}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Print CSS */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:px-8 { padding-left: 2rem !important; padding-right: 2rem !important; }
          .app-surface { box-shadow: none !important; border: 1px solid #e2e8f0 !important; break-inside: avoid; }
        }
      `}</style>
    </div>
    </FieldAppShell>
  )
}
